import { useCallback, useEffect, useRef, useState } from "react";

import { BaseAnchorProps } from "./types";

import "./styles.css";

const SCROLL_SPY_IGNORE_MS = 1200;
const DEFAULT_OFFSET_TOP = 100;

const BaseAnchor = ({
  items,
  offsetTop = DEFAULT_OFFSET_TOP,
  className,
}: BaseAnchorProps) => {
  const [activeLink, setActiveLink] = useState<string>("");
  const containerRef = useRef<HTMLDivElement>(null);
  const linkRefsRef = useRef<Record<string, HTMLAnchorElement | null>>({});
  const ignoreScrollSpyUntilRef = useRef(0);

  const computeActiveLink = useCallback((): string | null => {
    if (Date.now() < ignoreScrollSpyUntilRef.current) return null;
    const sections = items
      .map(({ href }) => ({
        href,
        el: document.querySelector(href),
      }))
      .filter((s): s is { href: string; el: Element } => s.el != null);
    if (sections.length === 0) return null;

    const activeLine = window.innerHeight / 2;
    const withBounds = sections.map(({ href, el }) => {
      const rect = el.getBoundingClientRect();
      return { href, top: rect.top, bottom: rect.bottom };
    });

    const containingCenter = withBounds.filter(
      ({ top, bottom }) => top <= activeLine && bottom >= activeLine,
    );
    if (containingCenter.length > 0) {
      const active = containingCenter.reduce((a, b) =>
        a.top >= b.top ? a : b,
      );
      return active.href;
    }

    const first = withBounds[0]!;
    const last = withBounds[withBounds.length - 1]!;
    if (activeLine < first.top) return first.href;
    if (activeLine > last.bottom) return last.href;

    const withMidpoint = withBounds.map((s) => ({
      ...s,
      mid: (s.top + s.bottom) / 2,
    }));
    const closest = withMidpoint.reduce((a, b) =>
      Math.abs(a.mid - activeLine) <= Math.abs(b.mid - activeLine) ? a : b,
    );
    return closest.href;
  }, [items]);

  useEffect(() => {
    const sectionElements = items
      .map(({ href }) => document.querySelector(href))
      .filter((el): el is Element => el != null);
    if (sectionElements.length === 0) return;

    const updateActiveFromIntersection = () => {
      const nextActive = computeActiveLink();
      if (nextActive != null) {
        setActiveLink((current) =>
          current !== nextActive ? nextActive : current,
        );
      }
    };

    const observer = new IntersectionObserver(
      () => updateActiveFromIntersection(),
      {
        root: null,
        rootMargin: "-1px 0px 0px 0px",
        threshold: [0, 0.01, 0.5, 1],
      },
    );

    sectionElements.forEach((el) => observer.observe(el));
    updateActiveFromIntersection();

    const handleScroll = () => updateActiveFromIntersection();
    window.addEventListener("scroll", handleScroll, {
      passive: true,
      capture: true,
    });

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll, { capture: true });
    };
  }, [items, computeActiveLink]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      e.preventDefault();
      setActiveLink(href);
      ignoreScrollSpyUntilRef.current = Date.now() + SCROLL_SPY_IGNORE_MS;
      const target = document.querySelector(href);
      if (target) {
        const y =
          target.getBoundingClientRect().top +
          (window.scrollY ?? document.documentElement.scrollTop) -
          offsetTop;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    },
    [offsetTop],
  );

  return (
    <div ref={containerRef} className={`base-anchor ${className ?? ""}`.trim()}>
      <div className="base-anchor-links">
        {items.map(({ key, href, title }) => (
          <div
            key={key}
            className={`base-anchor-link ${activeLink === href ? "base-anchor-link-active" : ""}`}
          >
            <a
              ref={(el) => {
                linkRefsRef.current[href] = el;
              }}
              href={href}
              className="base-anchor-link-title"
              onClick={(e) => handleClick(e, href)}
            >
              {title}
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BaseAnchor;
